<script setup lang="ts">
import { onMounted, ref, watch, computed } from 'vue'
import { formatDayByDate, formatTimeByDate } from '@/common/utils/formatDate'
import { getSortedInspectionDates } from './helperFunctions'

interface PropTypes {
  inspections
  modelValue?
  disabledItem?
}
const props = defineProps<PropTypes>()

const emits = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const instanceStartDate = ref()
const inspectionDates = computed(() => {
  const sortedDates = getSortedInspectionDates(props.inspections, 'dsc')
  return sortedDates.map((inspection) => {
    return {
      disabled: props.disabledItem?.startDate === inspection.startDate,
      value: inspection.startDate,
      label: `${formatDayByDate(inspection.startDate)} ${formatTimeByDate(inspection.startDate)}`,
    }
  })
})

const currentInspectionIndex = computed(() => {
  if (!instanceStartDate.value) return 0
  return props.inspections.findIndex((inspection) => inspection.startDate === instanceStartDate.value)
})
const previousDisabled = computed(() => {
  let endOfList = currentInspectionIndex.value <= 0
  if (endOfList) return endOfList
  // if we have a diabled item next we should update the endOfList
  let nextInpection = props.inspections[currentInspectionIndex.value - 1]
  const skipNext = nextInpection.inspectionInstanceUuid === props.disabledItem?.inspectionInstanceUuid
  if (!endOfList && skipNext) {
    endOfList = currentInspectionIndex.value - 1 <= 0
  }
  return endOfList
})

const nextDisabled = computed(() => {
  let endOfList = currentInspectionIndex.value >= props.inspections.length - 1
  if (endOfList) return endOfList
  // if we have a diabled item next we should update the endOfList
  let nextInpection = props.inspections[currentInspectionIndex.value + 1]
  const skipNext = nextInpection.inspectionInstanceUuid === props.disabledItem?.inspectionInstanceUuid
  if (!endOfList && skipNext) {
    endOfList = currentInspectionIndex.value + 1 >= props.inspections.length - 1
  }
  return endOfList
})

const handleInput = (value) => {
  const inspectIndex = props.inspections.findIndex((inspection) => inspection.startDate === value)
  emits('update:modelValue', props.inspections[inspectIndex])
}

const previousHandler = () => {
  let nextInpection = props.inspections[currentInspectionIndex.value - 1]
  // if next item is a disabled item skip it
  if (nextInpection === props.disabledItem) {
    nextInpection = props.inspections[currentInspectionIndex.value - 2]
  }
  instanceStartDate.value = nextInpection
  emits('update:modelValue', nextInpection)
}
const nextHandler = () => {
  let nextInpection = props.inspections[currentInspectionIndex.value + 1]

  // if next item is a disabled item skip it
  if (nextInpection === props.disabledItem) {
    nextInpection = props.inspections[currentInspectionIndex.value + 2]
  }

  instanceStartDate.value = nextInpection
  emits('update:modelValue', nextInpection)
}

onMounted(() => {
  instanceStartDate.value = props.modelValue.startDate
})

watch(
  () => props.modelValue,
  (): void => {
    instanceStartDate.value = props.modelValue.startDate
  }
)
</script>

<template>
  <div>
    <el-button type="primary" round :disabled="previousDisabled" @click="previousHandler">Previous</el-button>
    <el-select-v2
      v-model="instanceStartDate"
      class="px-4"
      :options="inspectionDates"
      placeholder="Inspection Instance"
      size="large"
      style="width: 80%"
      @change="handleInput"
    />
    <el-button type="primary" round :disabled="nextDisabled" @click="nextHandler">Next</el-button>
  </div>
</template>
